/**
 * EF-PLATFORM-AUTHENTICATION-V1: Authentication Context
 * 
 * Provides authentication state management across the application.
 * Handles login, logout, token storage, and session verification.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

// Auth context
const AuthContext = createContext(null);

// Token storage key
const TOKEN_KEY = "eventsflo_auth_token";
const USER_KEY = "eventsflo_auth_user";

/**
 * Get stored token from localStorage
 */
export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Get stored user from localStorage
 */
export function getStoredUser() {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

/**
 * Store auth data in localStorage
 */
function storeAuth(token, user) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (e) {
    console.error("Failed to store auth data:", e);
  }
}

/**
 * Clear auth data from localStorage
 */
function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (e) {
    console.error("Failed to clear auth data:", e);
  }
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Verify token and load user on mount
   */
  /**
   * EF-FIX-AUTH-VERIFY-AND-TOKEN-CONTEXT-V1:
   * Verify token and load user on mount.
   * Uses /auth/verify as SINGLE SOURCE OF TRUTH for user identity.
   */
  const verifySession = useCallback(async () => {
    const storedToken = getStoredToken();
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      // Set token in API client
      api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      
      // Verify token with backend - SINGLE SOURCE OF TRUTH
      const response = await api.get("/auth/verify");
      
      // EF-FIX-AUTH-VERIFY-AND-TOKEN-CONTEXT-V1: Map verify response to user object
      // Backend returns: user_id, email, user_type, role, company_id, staff_id, active
      const verifyData = response.data;
      const userData = {
        id: verifyData.user_id,  // Map user_id to id for compatibility
        user_id: verifyData.user_id,
        email: verifyData.email,
        user_type: verifyData.user_type,
        role: verifyData.role,
        company_id: verifyData.company_id,
        staff_id: verifyData.staff_id,
        active: verifyData.active,
      };
      
      // Store updated user data
      storeAuth(storedToken, userData);
      
      setUser(userData);
      setToken(storedToken);
      setError(null);
    } catch (err) {
      console.error("Session verification failed:", err);
      // Clear invalid token
      clearAuth();
      delete api.defaults.headers.common["Authorization"];
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Verify session on mount
  useEffect(() => {
    verifySession();
  }, [verifySession]);

  /**
   * Login function
   */
  const login = useCallback(async (email, password) => {
    setError(null);
    
    try {
      const response = await api.post("/auth/login", { email, password });

      // EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
      // If the backend returned a 2FA challenge instead of a session
      // JWT, surface it to the caller so LoginPage can render the
      // enroll/verify step. Do NOT persist any auth state yet.
      if (response.data?.totp_required) {
        return {
          success: false,
          totp_required: true,
          totp_action: response.data.totp_action,      // "enroll" | "verify"
          challenge_token: response.data.challenge_token,
          expires_in: response.data.expires_in,
        };
      }

      const { access_token, user: userData } = response.data;
      
      // Store auth data
      storeAuth(access_token, userData);
      
      // Set in API client
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Update state
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (err) {
      // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
      // Backend may return a structured detail object when the email
      // belongs to a different portal (Client Portal). Pass that
      // through so LoginPage can render guidance + redirect button.
      const raw = err.response?.data?.detail;
      if (raw && typeof raw === "object" && raw.error === "wrong_portal_client_account") {
        const msg = raw.message || "This email belongs to the Client Portal.";
        setError(msg);
        return {
          success: false,
          error: msg,
          portal_mismatch: {
            error: raw.error,
            redirect: raw.redirect || "/client/login",
          },
        };
      }
      const message =
        (typeof raw === "string" && raw) || raw?.message || "Login failed";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  /**
   * EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
   * Complete the second-factor step of a SuperAdmin login.
   *
   * @param {Object} opts
   * @param {string} opts.challenge_token - short-lived challenge JWT
   * @param {"enroll"|"verify"} opts.action
   * @param {string} [opts.code]           - 6-digit TOTP code
   * @param {string} [opts.recovery_code]  - one-time recovery code
   * @param {string} [opts.enrollSecret]   - only for enroll: caller
   *   already called /auth/totp/enroll/start and holds the secret; if
   *   omitted, the caller must have already scanned the QR.
   *
   * On success: persists the session JWT and returns
   *   { success: true, user, recovery_codes? }.
   * On failure: returns { success: false, error }.
   */
  const completeTotp = useCallback(async ({ challenge_token, action, code, recovery_code }) => {
    setError(null);
    const endpoint = action === "enroll" ? "/auth/totp/enroll/verify" : "/auth/totp/verify";
    try {
      const body = code ? { code } : { recovery_code };
      const resp = await api.post(endpoint, body, {
        headers: { Authorization: `Bearer ${challenge_token}` },
      });
      const { access_token, user: userData, recovery_codes } = resp.data;
      storeAuth(access_token, userData);
      api.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      return { success: true, user: userData, recovery_codes };
    } catch (err) {
      const raw = err.response?.data?.detail;
      const message = (typeof raw === "string" && raw) || raw?.message || "Invalid code";
      setError(message);
      return { success: false, error: message };
    }
  }, []);

  /**
   * EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
   * Kick off TOTP enrollment for a SuperAdmin who just passed the
   * password step. Returns { secret, otpauth_uri, qr_png_data_url }.
   */
  const startTotpEnrollment = useCallback(async ({ challenge_token }) => {
    try {
      const resp = await api.post(
        "/auth/totp/enroll/start",
        {},
        { headers: { Authorization: `Bearer ${challenge_token}` } }
      );
      return { success: true, ...resp.data };
    } catch (err) {
      const raw = err.response?.data?.detail;
      const message = (typeof raw === "string" && raw) || raw?.message || "Enrollment failed";
      return { success: false, error: message };
    }
  }, []);

  /**
   * Logout function
   *
   * EVENTSFLO-PWA-LOGOUT-ROLE-ROUTING-SECURITY-AUDIT-FIX-V1: also clear any
   * crew PWA session cookie so the symmetric case (admin → PWA preview →
   * admin logout) doesn't leave a stale crew identity behind.
   */
  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout API call failed:", err);
    }
    clearAuth();
    delete api.defaults.headers.common["Authorization"];
    try {
      // Strip the crew PWA cookie set by CrewContext (icrew_session).
      document.cookie = "icrew_session=; path=/; max-age=0";
      localStorage.removeItem("auth_data");
      sessionStorage.removeItem("icrew:sw-reloaded-for");
    } catch {
      /* ignore */
    }
    setUser(null);
    setToken(null);
    setError(null);
  }, []);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  }, [user]);

  /**
   * Check if user is superadmin
   */
  const isSuperAdmin = useCallback(() => {
    return user?.role === "superadmin";
  }, [user]);

  /**
   * Check if user is tenant admin
   */
  const isTenantAdmin = useCallback(() => {
    return user?.role === "tenant_admin";
  }, [user]);

  /**
   * Check if user is any kind of admin
   */
  const isAdmin = useCallback(() => {
    return user?.role === "superadmin" || user?.role === "tenant_admin";
  }, [user]);

  // Context value
  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    // EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
    completeTotp,
    startTotpEnrollment,
    verifySession,
    hasRole,
    isSuperAdmin,
    isTenantAdmin,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * HOC for protected routes
 */
export function withAuth(Component, requiredRole = null) {
  return function ProtectedComponent(props) {
    const { isAuthenticated, loading, hasRole } = useAuth();
    
    if (loading) {
      return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }
    
    if (!isAuthenticated) {
      window.location.href = "/login";
      return null;
    }
    
    if (requiredRole && !hasRole(requiredRole)) {
      return <div className="flex items-center justify-center h-screen">Access Denied</div>;
    }
    
    return <Component {...props} />;
  };
}

export default AuthContext;
